// components/AwsPricingExport.jsx — ekspor estimate AKTIF ke Excel (gaya
// proposal, dua sheet berformula) dan PDF.
//
// CAKUPAN. Hanya isi keranjang yang sedang tampil di tab Kalkulator. Baris
// "opsional bernilai nol" seperti pada berkas proposal contoh SENGAJA tidak
// dibuat: baris kosong yang dibiarkan nol mudah dikira sudah diperhitungkan.
//
// DOKUMEN BERBAHASA INGGRIS. Isi workbook di-hardcode Inggris karena ini
// proposal biaya untuk manajemen; hanya label tombol yang mengikuti i18n.
//
// DUA SHEET, DAN INI INTI NILAINYA. 'Assumptions' memuat seluruh angka masukan;
// 'Cost Comparison Report' hampir seluruhnya FORMULA yang menunjuk ke sana.
// Mengubah satu sel di Assumptions membuat Excel menghitung ulang laporannya,
// sehingga pembaca bisa menguji skenario tanpa membuka dashboard.
//
// ANGKA TIDAK DIHITUNG ULANG DI SINI. computeLine milik kalkulator adalah
// satu-satunya sumber kebenaran; berkas ini menerimanya lewat props. Yang
// ditulis ke Excel adalah HARGA SATUAN hasil computeLine, lalu formula
// mengalikannya kembali — nilai bawaan workbook selalu sama dengan layar.
import React, { useState } from 'react';
import { FileSpreadsheet, FileText, Sparkles, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import { api } from '../context/AuthContext';

// Label tombol memakai i18n bila key-nya ada, jika tidak jatuh ke teks Inggris.
// Mencegah key mentah muncul bila translations.js belum diperbarui, tanpa
// menghalangi terjemahan yang benar begitu key ditambahkan.
const tf = (t, key, fallback) => {
  try { const v = t(key); return (v && v !== key) ? v : fallback; } catch (_) { return fallback; }
};

// ── Gaya sel ────────────────────────────────────────────────────────────────
const B = (c = 'D1D5DB') => ({
  top: { style: 'thin', color: { rgb: c } }, bottom: { style: 'thin', color: { rgb: c } },
  left: { style: 'thin', color: { rgb: c } }, right: { style: 'thin', color: { rgb: c } },
});
const MONEY = '"$"#,##0.00';
const MONEY4 = '"$"#,##0.0000';
const IDR = '"Rp" #,##0';
const PCT = '0.0%';

const S = {
  title:   { font: { bold: true, sz: 16, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '0F766E' } },
             alignment: { vertical: 'center', horizontal: 'left', indent: 1 } },
  sub:     { font: { sz: 10, color: { rgb: 'E6FFFA' } }, fill: { fgColor: { rgb: '0F766E' } },
             alignment: { vertical: 'center', horizontal: 'left', indent: 1 } },
  head:    { font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '0D9488' } },
             alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: B() },
  section: { font: { bold: true, sz: 11, color: { rgb: '134E4A' } }, fill: { fgColor: { rgb: 'CCFBF1' } }, border: B() },
  text:    { font: { sz: 10 }, border: B(), alignment: { vertical: 'center', wrapText: true } },
  dim:     { font: { sz: 9, color: { rgb: '6B7280' } }, border: B(), alignment: { vertical: 'center' } },
  num:     { font: { sz: 10 }, border: B(), numFmt: MONEY,  alignment: { horizontal: 'right' } },
  num4:    { font: { sz: 10 }, border: B(), numFmt: MONEY4, alignment: { horizontal: 'right' } },
  int:     { font: { sz: 10 }, border: B(), numFmt: '#,##0.##', alignment: { horizontal: 'right' } },
  pct:     { font: { sz: 10 }, border: B(), numFmt: PCT, alignment: { horizontal: 'right' } },
  subN:    { font: { bold: true, sz: 10 }, border: B(), numFmt: MONEY, alignment: { horizontal: 'right' },
             fill: { fgColor: { rgb: 'F1F5F9' } } },
  subP:    { font: { bold: true, sz: 10 }, border: B(), numFmt: PCT, alignment: { horizontal: 'right' },
             fill: { fgColor: { rgb: 'F1F5F9' } } },
  subL:    { font: { bold: true, sz: 10 }, border: B(), fill: { fgColor: { rgb: 'F1F5F9' } } },
  totalL:  { font: { bold: true, sz: 12, color: { rgb: '134E4A' } }, border: B('0D9488'),
             fill: { fgColor: { rgb: 'CCFBF1' } } },
  total:   { font: { bold: true, sz: 12, color: { rgb: '134E4A' } }, border: B('0D9488'), numFmt: MONEY,
             fill: { fgColor: { rgb: 'CCFBF1' } }, alignment: { horizontal: 'right' } },
  idr:     { font: { sz: 10, italic: true, color: { rgb: '6B7280' } }, border: B(), numFmt: IDR,
             alignment: { horizontal: 'right' } },
  // Sel masukan berwarna amber: satu-satunya penanda mana yang boleh diubah
  // pembaca. Tanpa ini seluruh workbook tampak sama-sama bisa disunting.
  input:   { font: { bold: true, sz: 10, color: { rgb: '92400E' } }, fill: { fgColor: { rgb: 'FEF3C7' } },
             border: B('F59E0B'), alignment: { horizontal: 'right' } },
  inputT:  { font: { bold: true, sz: 10, color: { rgb: '92400E' } }, fill: { fgColor: { rgb: 'FEF3C7' } },
             border: B('F59E0B') },
  label:   { font: { sz: 10 }, border: B() },
  labelB:  { font: { bold: true, sz: 10 }, border: B() },
  note:    { font: { sz: 9, italic: true, color: { rgb: '6B7280' } }, alignment: { wrapText: true, vertical: 'top' } },
  aiHead:  { font: { bold: true, sz: 11, color: { rgb: '7C2D12' } }, fill: { fgColor: { rgb: 'FFEDD5' } }, border: B('FB923C') },
  aiBody:  { font: { sz: 10, color: { rgb: '431407' } }, alignment: { wrapText: true, vertical: 'top' }, border: B('FED7AA') },
  warn:    { font: { sz: 9, color: { rgb: 'B45309' } }, border: B(), alignment: { wrapText: true } },
};

const txt = (v, s) => ({ v: v === null || v === undefined ? '' : String(v), t: 's', s });
const num = (v, s) => ({ v: Number(v) || 0, t: 'n', s });
const fml = (f, s) => ({ t: 'n', f, s });
const blank = () => ({ v: '', t: 's' });
const pad = (n, s) => Array.from({ length: n }, () => txt('', s));

const stamp = () => {
  const d = new Date(); const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
};
const nowText = () => new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;')
  .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const fmt = (v, d = 2) => (Number(v) || 0).toLocaleString('en-US',
  { minimumFractionDigits: d, maximumFractionDigits: d });

const LEASE_YEARS = (lease) => (lease === '3yr' ? 3 : 1);

/**
 * Meratakan keranjang menjadi bentuk tunggal yang dipakai Excel, PDF, dan
 * konteks AI — supaya ketiganya tidak bisa menyimpang.
 *
 * `tiered` menandai baris berharga jenjang. Untuk baris itu harga satuan
 * TUNGGAL tidak mewakili biayanya, jadi formula memakai nilai bulanan hasil
 * hitung dan kolom harga satuan dikosongkan. Menuliskan tarif tier pertama
 * seolah berlaku untuk seluruh volume akan salah tanpa terlihat salah.
 */
function buildRows({ lines, computeLine, hoursPerMonth }) {
  return lines.map((l, i) => {
    const c = computeLine(l, hoursPerMonth) || {};
    const isCompute = l.kind !== 'usage';
    const qty = Number(l.qty) || 0;
    const units = isCompute ? (Number(l.hoursPerMonth ?? hoursPerMonth) || 0) : (Number(l.volumeGb) || 0);
    const tiers = (l.rates && l.rates.tiers) || [];
    const tiered = !isCompute && tiers.length > 1;

    const choice = l.choice || { kind: 'od' };
    const commitment = choice.kind === 'od' ? 'On-Demand'
      : choice.kind === 'ri' ? `RI ${choice.offering} ${choice.lease} ${choice.payment}`
      : `SP ${choice.plan_type} ${choice.lease} ${choice.payment}`;

    const odUnit = isCompute ? (Number(l.rates && l.rates.onDemand && l.rates.onDemand.hourly) || 0)
      : (tiered ? 0 : (Number(tiers[0] && tiers[0].price_per_unit) || 0));
    const selUnit = isCompute ? (Number(c.hourly) || 0) : odUnit;

    const flags = [];
    if (c.unavailable) flags.push('no price in catalog');
    if (c.derived) flags.push('derived cash flow');
    if (c.anomaly) flags.push('catalog anomaly');
    if (l.drift) flags.push('price drifted since saved');
    if (tiered) flags.push(`tiered (${tiers.length} tiers)`);

    return {
      no: i + 1, isCompute,
      kind: l.kind === 'ec2' ? 'EC2' : l.kind === 'rds' ? 'RDS'
        : l.kind === 'opensearch' ? 'OpenSearch' : 'Usage',
      label: l.label || '',
      detail: [l.usage_type, l.operation].filter(Boolean).join(' · '),
      commitment,
      leaseYears: choice.kind === 'od' ? null : LEASE_YEARS(choice.lease),
      qty, units,
      unit: isCompute ? 'hours' : ((tiers[0] && tiers[0].unit) || 'units'),
      tiered, odUnit, selUnit,
      choice,
      upfrontPerUnit: qty > 0 ? (Number(c.cashUpfront) || 0) / qty : 0,
      monthly: Number(c.recurring) || 0,
      odMonthly: tiered ? null : qty * odUnit * units,
      upfront: Number(c.cashUpfront) || 0,
      flags: flags.join('; '),
      matrix: Array.isArray(l.rates && l.rates.matrix) ? l.rates.matrix : [],
    };
  });
}

// Pembanding On-Demand untuk baris berjenjang tidak bisa diturunkan dari harga
// satuan; dihitung dengan menjalankan ulang tiering pada volume yang sama.
function tieredOd(row, lines, computeLine, hoursPerMonth) {
  if (!row.tiered) return row.odMonthly;
  const l = lines[row.no - 1];
  if (!l) return null;
  const c = computeLine({ ...l, choice: { kind: 'od' } }, hoursPerMonth) || {};
  return Number(c.recurring) || 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// Lembar Re-import
// ─────────────────────────────────────────────────────────────────────────────
//
// Kolom di sini SENGAJA memakai nama field kandidat apa adanya (kind,
// instance_type, usage_key, …) supaya pemetaan otomatis di tab Import mengenali
// seluruhnya tanpa pengguna memilih satu pun kolom. Nama yang "lebih rapi untuk
// dibaca manusia" akan memutus pengenalan itu — lembar ini alat, bukan bagian
// proposal, dan itulah sebabnya ia disembunyikan.
//
// APA YANG TIDAK IKUT, dan tidak bisa: pilihan komitmen (RI/SP) serta
// hours_per_month. Jalur impor menghasilkan baris On-Demand dan memakai jam
// bawaan konfigurasi, karena validator katalog tidak menerima kedua field itu.
// Re-impor karena itu memulihkan SUSUNAN keranjang, bukan skenario harganya.
//
// BARIS USAGE MANUAL TIDAK BISA DI-ROUND-TRIP. usage_key hanya ada pada baris
// yang asalnya dari impor; baris yang dirakit lewat Add line dibiarkan kosong
// dan akan ditolak (merah) bila lembar ini diimpor. Dikosongkan, bukan ditebak
// balik dari usage_type: pembalikan itu menuntut membalik USAGE_MAP, dan tabel
// itu ditulis eksplisit di backend karena katalog tidak bisa ditafsirkan dari
// usage_type saja.
const REIMPORT_COLS = ['kind', 'source_text', 'instance_type', 'os', 'tenancy',
  'pre_installed_sw', 'db_engine', 'db_edition', 'deployment_option',
  'license_model', 'usage_key', 'amount', 'qty'];

function buildReimportSheet(lines) {
  const head = { font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
                 fill: { fgColor: { rgb: '475569' } }, border: B() };
  const A = [];
  A.push([txt('RE-IMPORT — machine-readable copy of the cart', { font: { bold: true, sz: 12 } })]);
  A.push([txt('Upload this file on the Import tab. Column headers match the importer\'s field '
    + 'names and map automatically. Commitment choice (RI/SP) and hours per month are NOT '
    + 'carried — re-imported rows come back as On-Demand at the default hours. Usage rows '
    + 'built by hand have no usage_key and will be rejected; only imported usage rows and '
    + 'all compute rows round-trip.', S.note)]);
  A.push([]);
  A.push(REIMPORT_COLS.map(c => txt(c, head)));

  let manual = 0;
  lines.forEach((l) => {
    const usage = l.kind === 'usage';
    if (usage && !l.usage_key) manual += 1;
    const v = {
      kind: l.kind || '',
      // Kutipan sumber asli bila baris ini datang dari impor; kalau tidak,
      // label keranjang. Kolom ini tidak dipakai untuk menghitung apa pun —
      // ia yang membuat baris bisa dicocokkan kembali oleh manusia.
      source_text: l.importedFrom || l.label || '',
      instance_type: l.instance_type || '',
      os: l.os || '',
      tenancy: l.tenancy || '',
      pre_installed_sw: l.pre_installed_sw || '',
      db_engine: l.db_engine || '',
      db_edition: l.db_edition || '',
      deployment_option: l.deployment_option || '',
      license_model: l.license_model || '',
      usage_key: l.usage_key || '',
      amount: usage ? (Number(l.volumeGb) || '') : '',
      qty: Number(l.qty) || 1,
    };
    A.push(REIMPORT_COLS.map(c => (
      (c === 'amount' || c === 'qty')
        ? (v[c] === '' ? txt('', S.label) : num(v[c], S.int))
        : txt(v[c], S.label))));
  });

  if (manual) {
    A.push([]);
    A.push([txt(`${manual} usage row(s) have no usage_key because they were added by hand. `
      + 'Those rows cannot be re-imported — rebuild them on the Calculator tab.', S.warn)]);
  }

  const ws = XLSX.utils.aoa_to_sheet(A);
  ws['!cols'] = REIMPORT_COLS.map(c => ({ wch: c === 'source_text' ? 40 : Math.max(11, c.length + 2) }));
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 12 } },
                   { s: { r: 1, c: 0 }, e: { r: 1, c: 12 } }];
  ws['!rows'] = [undefined, { hpt: 58 }];
  return ws;
}

// ─────────────────────────────────────────────────────────────────────────────
// EXCEL
// ─────────────────────────────────────────────────────────────────────────────
function exportExcel(ctx) {
  const { lines, computeLine, hoursPerMonth, region, supportPlan, vatCountry,
          fxRate, totals, narrative } = ctx;
  const rows = buildRows(ctx);
  const termYears = Math.max(1, ...rows.map(r => r.leaseYears || 1));

  // ── Sheet Assumptions ───────────────────────────────────────────────────
  // Dibangun LEBIH DULU karena laporan menunjuk ke nomor barisnya; menyusunnya
  // belakangan berarti menebak alamat sel.
  const A = [];
  const push = (cells) => { A.push(cells); return A.length; };

  push([txt('ASSUMPTIONS & INPUTS', S.title), ...pad(8, S.title)]);
  push([txt('Amber cells are inputs. Change them and the Cost Comparison Report recalculates.', S.note)]);
  push([blank()]);
  push([txt('Global', S.section), ...pad(8, S.section)]);
  const rRegion  = push([txt('Region', S.label), txt(region, S.inputT)]);
  const rHours   = push([txt('Hours per month', S.label), num(hoursPerMonth, S.input)]);
  const rTerm    = push([txt('Term (years)', S.label), num(termYears, S.input)]);
  const rSupport = push([txt(`Support — ${supportPlan}  ($/month)`, S.label), num(totals.support, S.input)]);
  const rVat     = push([txt(`VAT — ${vatCountry} (%)`, S.label), num(totals.vatPct, S.input)]);
  const rFx      = push([txt('USD to IDR rate', S.label), num(fxRate, S.input)]);
  push([txt('Generated', S.label), txt(nowText(), S.dim)]);
  push([blank()]);
  push([txt('Per-line unit prices', S.section), ...pad(8, S.section)]);
  push(['#', 'Item', 'Qty', 'Units / month', 'Unit', 'On-Demand unit price',
        'Selected unit price', 'Upfront per unit', 'Notes',
        'Tiered: On-Demand $/mo', 'Tiered: selected $/mo'].map(h => txt(h, S.head)));

  const firstLineRow = A.length + 1;
  rows.forEach(r => {
    const odM = tieredOd(r, lines, computeLine, hoursPerMonth);
    const row = [
      num(r.no, S.int), txt(`${r.kind} · ${r.label}`, S.text), num(r.qty, S.input),
      num(r.units, S.input), txt(r.unit, S.dim),
      r.tiered ? txt('tiered', S.dim) : num(r.odUnit, S.input),
      r.tiered ? txt('tiered', S.dim) : num(r.selUnit, S.input),
      num(r.upfrontPerUnit, S.input),
      txt(r.flags, r.flags ? S.warn : S.dim),
    ];
    if (r.tiered) { row.push(num(odM || 0, S.input)); row.push(num(r.monthly, S.input)); }
    A.push(row);
  });

  A.push([blank()]);
  A.push([txt('Columns J and K carry the computed monthly cost for TIERED lines only. A tiered line has no single '
    + 'unit price — writing the first-tier rate as if it applied to the whole volume would be wrong without looking '
    + 'wrong — so the report reads those two cells instead. Editing "Units / month" will NOT re-tier them.', S.note)]);
  A.push([txt('Support follows the convention used by the calculator: its basis is the recurring monthly cost and '
    + 'EXCLUDES reserved-instance upfront payments, matching how AWS bills it.', S.note)]);

  const wsA = XLSX.utils.aoa_to_sheet(A);
  wsA['!cols'] = [{ wch: 5 }, { wch: 46 }, { wch: 8 }, { wch: 14 }, { wch: 10 },
                  { wch: 18 }, { wch: 18 }, { wch: 16 }, { wch: 30 }, { wch: 20 }, { wch: 20 }];
  wsA['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 8 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 8 } },
    { s: { r: A.length - 2, c: 0 }, e: { r: A.length - 2, c: 8 } },
    { s: { r: A.length - 1, c: 0 }, e: { r: A.length - 1, c: 8 } },
  ];
  wsA['!rows'] = A.map((row) => (row[0] && row[0].s === S.note ? { hpt: 30 } : undefined));

  const G = {
    term: `Assumptions!$B$${rTerm}`,
    supp: `Assumptions!$B$${rSupport}`,
    vat:  `Assumptions!$B$${rVat}`,
    fx:   `Assumptions!$B$${rFx}`,
  };
  void rRegion; void rHours;   // dipakai lewat kolom per-baris, bukan langsung

  // ── Sheet Cost Comparison Report ────────────────────────────────────────
  const R = [];
  R.push([txt(`AWS Cost Proposal — ${region}`, S.title), ...pad(8, S.title)]);
  R.push([txt(`Generated ${nowText()}   ·   Term ${termYears} year(s)   ·   Support: ${supportPlan}   ·   VAT: ${vatCountry}`,
    S.sub), ...pad(8, S.sub)]);
  R.push([blank()]);
  R.push(['Item', 'Commitment', 'Qty', 'Units / mo', 'Unit', 'On-Demand $/mo',
          'Selected $/mo', 'Upfront (month 0)', 'Saving over term']
    .map(h => txt(h, S.head)));

  const compute = rows.filter(r => r.isCompute);
  const usage   = rows.filter(r => !r.isCompute);

  const emitLine = (r) => {
    const ar = firstLineRow + (r.no - 1);
    const qty = `Assumptions!$C$${ar}`, un = `Assumptions!$D$${ar}`;
    const odU = `Assumptions!$F$${ar}`, selU = `Assumptions!$G$${ar}`, upU = `Assumptions!$H$${ar}`;
    const odExpr  = r.tiered ? `Assumptions!$J$${ar}` : `${qty}*${un}*${odU}`;
    const selExpr = r.tiered ? `Assumptions!$K$${ar}` : `${qty}*${un}*${selU}`;
    R.push([
      txt(`${r.kind} · ${r.label}${r.detail ? `\n${r.detail}` : ''}`, S.text),
      txt(r.commitment, S.text),
      fml(qty, S.int), fml(un, S.int), txt(r.unit, S.dim),
      fml(odExpr, S.num), fml(selExpr, S.num), fml(`${qty}*${upU}`, S.num),
      // Saving diukur atas TOTAL MASA SEWA, bukan biaya berulang saja.
      // Basis berulang membuat opsi All Upfront tampak hemat 100% karena
      // tarif per jamnya nol — padahal uangnya sudah dibayar di muka. Pada
      // contoh uji: 100% (menyesatkan) vs 64,3% (benar).
      fml(`IF((${odExpr})*12*${G.term}=0,0,`
        + `1-((${qty}*${upU})+(${selExpr})*12*${G.term})/((${odExpr})*12*${G.term}))`, S.pct),
    ]);
  };

  const subtotal = (label, firstR, lastR) => {
    R.push([
      txt(label, S.subL), ...pad(4, S.subL),
      fml(`SUM(F${firstR}:F${lastR})`, S.subN),
      fml(`SUM(G${firstR}:G${lastR})`, S.subN),
      fml(`SUM(H${firstR}:H${lastR})`, S.subN),
      fml(`IF(SUM(F${firstR}:F${lastR})*12*${G.term}=0,0,`
        + `1-(SUM(H${firstR}:H${lastR})+SUM(G${firstR}:G${lastR})*12*${G.term})`
        + `/(SUM(F${firstR}:F${lastR})*12*${G.term}))`, S.subP),
    ]);
    return R.length;
  };

  const subRows = [];
  if (compute.length) {
    R.push([txt('COMPUTE', S.section), ...pad(8, S.section)]);
    const first = R.length + 1;
    compute.forEach(emitLine);
    subRows.push(subtotal('Subtotal — compute', first, R.length));
  }
  if (usage.length) {
    R.push([txt('STORAGE, NETWORK & OTHER SERVICES', S.section), ...pad(8, S.section)]);
    const first = R.length + 1;
    usage.forEach(emitLine);
    subRows.push(subtotal('Subtotal — other services', first, R.length));
  }
  const sumOf = (col) => (subRows.length ? subRows.map(r => `${col}${r}`).join('+') : '0');

  R.push([blank()]);
  const kv = (label, formula, style, lstyle) => {
    R.push([txt(label, lstyle || S.labelB), ...pad(5, S.label),
            fml(formula, style || S.num), ...pad(2, S.label)]);
    return R.length;
  };

  const rRecur = kv('Monthly recurring', sumOf('G'));
  const rSupp  = kv(`Support — ${supportPlan}`, G.supp);
  const rVatR  = kv(`VAT ${vatCountry}`, `(G${rRecur}+G${rSupp})*${G.vat}/100`);
  R.push([txt('TOTAL PER MONTH', S.totalL), ...pad(5, S.totalL),
          fml(`G${rRecur}+G${rSupp}+G${rVatR}`, S.total), ...pad(2, S.totalL)]);
  const rTotal = R.length;
  R.push([txt('Equivalent in IDR', S.label), ...pad(5, S.label),
          fml(`G${rTotal}*${G.fx}`, S.idr), ...pad(2, S.label)]);
  const rCash = kv('Cash upfront (month 0)', sumOf('H'));
  kv(`Total cost over term  =  upfront + monthly × 12 × ${termYears}`,
    `G${rCash}+G${rTotal}*12*${G.term}`, { ...S.num, font: { bold: true, sz: 10 } });
  kv('On-Demand equivalent per month (comparison only)', sumOf('F'));

  R.push([blank()]);
  R.push([txt('Monthly totals follow the AWS Pricing Calculator convention: recurring cost only. Upfront is NOT '
    + 'amortised into the monthly figure — it is shown separately as a month-0 outflow, and again inside the '
    + 'total-over-term line.', S.note), ...pad(8, S.note)]);
  R.push([txt('The Support basis excludes reserved-instance upfront, matching how AWS bills it.', S.note), ...pad(8, S.note)]);
  R.push([txt('"Saving over term" compares the FULL cost of each option over the term — upfront plus monthly × 12 × '
    + 'term — against the same period on On-Demand. Measuring saving on the recurring figure alone would report an '
    + 'All Upfront reservation as 100% saved, because its hourly rate is zero once the money has been paid.', S.note),
    ...pad(8, S.note)]);
  R.push([txt('Every figure on this sheet is a formula pointing at the Assumptions sheet. Change an input there and '
    + 'this report recalculates.', S.note), ...pad(8, S.note)]);

  // ── Opsi komitmen alternatif ────────────────────────────────────────────
  // Berkas proposal contoh mengetik tabel ini tangan. Di sini ia dihasilkan dari
  // matriks yang sudah dimiliki kalkulator, jadi tidak bisa basi.
  const withMatrix = compute.filter(r => r.matrix.length);
  if (withMatrix.length) {
    R.push([blank()]);
    R.push([txt('ALTERNATIVE COMMITMENT OPTIONS', S.section), ...pad(8, S.section)]);
    R.push([txt('Generated from the live price catalog. Combinations AWS does not offer for a resource are omitted, '
      + 'with the omitted count stated under each table. The row highlighted and marked SELECTED ABOVE is the one '
      + 'used in the totals. These are static figures, not formulas.', S.note), ...pad(8, S.note)]);
    withMatrix.forEach(r => {
      // Baris 'not in catalog' DIBUANG. Untuk sumber daya seperti Ubuntu Pro
      // yang tidak punya RI, 12 dari 24 sel kosong — panjang tanpa menambah
      // informasi, dan mendorong grup yang berguna keluar dari halaman.
      // Grup yang TIDAK dipilih tetap ditampilkan: opsi yang lebih murah dari
      // yang dipilih adalah informasi paling relevan bagi pengambil keputusan,
      // jadi menyaring sampai hanya grup terpilih akan menutupinya.
      const priced = r.matrix.filter(c => !c.missing);
      const dropped = r.matrix.length - priced.length;
      if (!priced.length) return;
      R.push([txt(`${r.kind} · ${r.label}  (qty ${r.qty}, ${fmt(r.units, 0)} ${r.unit}/mo)`, S.labelB), ...pad(8, S.label)]);
      R.push(['Option', 'Term', 'Payment', 'Upfront', '$/hour', 'Monthly',
              'Total over term', 'Saving over term', 'Notes'].map(h => txt(h, S.head)));
      priced.forEach(c => {
        const yrs = LEASE_YEARS(c.lease_length);
        const monthly = r.qty * (Number(c.hourly) || 0) * r.units;
        const upfront = r.qty * (Number(c.upfront) || 0);
        const odMonthly = r.qty * r.odUnit * r.units;
        // Apakah sel ini yang dipakai di tabel utama. Tanpa penanda ini pembaca
        // harus mencocokkan sendiri teks komitmen di dua tempat.
        const ch = r.choice || {};
        const isSel = ch.kind === c.kind && ch.lease === c.lease_length && ch.payment === c.payment
          && (c.kind === 'ri' ? ch.offering === c.offering : ch.plan_type === c.plan_type);
        const cs = isSel ? { ...S.text, fill: { fgColor: { rgb: 'D1FAE5' } }, font: { bold: true, sz: 10 } } : S.text;
        const cn = isSel ? { ...S.num, fill: { fgColor: { rgb: 'D1FAE5' } }, font: { bold: true, sz: 10 } } : S.num;
        const c4 = isSel ? { ...S.num4, fill: { fgColor: { rgb: 'D1FAE5' } }, font: { bold: true, sz: 10 } } : S.num4;
        const cp = isSel ? { ...S.pct, fill: { fgColor: { rgb: 'D1FAE5' } }, font: { bold: true, sz: 10 } } : S.pct;
        R.push([
          txt(c.group, cs), txt(c.lease_length, cs), txt(c.payment, cs),
          num(upfront, cn), num(Number(c.hourly) || 0, c4), num(monthly, cn),
          num(upfront + monthly * 12 * yrs, cn),
          // Basis yang sama dengan tabel utama: total masa sewa, bukan bulanan.
          odMonthly > 0
            ? num(1 - (upfront + monthly * 12 * yrs) / (odMonthly * 12 * yrs), cp)
            : txt('', S.dim),
          txt([isSel ? 'SELECTED ABOVE' : '', c.derived ? 'derived cash flow' : '',
               c.anomaly ? 'anomaly' : ''].filter(Boolean).join('; '), S.warn),
        ]);
      });
      if (dropped > 0) {
        // Jumlah yang dibuang tetap dilaporkan. Menyaring baris tanpa memberi
        // tahu berapa yang hilang membuat tabel tampak seperti daftar lengkap.
        R.push([txt(`${dropped} of ${r.matrix.length} commitment combinations are not offered by AWS for this `
          + `resource and are omitted above.`, S.note), ...pad(8, S.note)]);
      }
      R.push([blank()]);
    });
  }

  // ── Blok naratif AI ─────────────────────────────────────────────────────
  // Hanya muncul bila pengguna menekan tombol Narrative DAN panggilan berhasil.
  // Kegagalan AI tidak boleh menghalangi angka yang sudah benar sampai ke
  // pembaca, jadi ketiadaannya diam saja — bukan pesan galat di dokumen resmi.
  if (narrative && narrative.trim()) {
    R.push([blank()]);
    R.push([txt('AI-GENERATED COMMENTARY — REVIEW BEFORE USE', S.aiHead), ...pad(8, S.aiHead)]);
    R.push([txt('Written by a language model from the figures in this workbook. The numbers above are computed by '
      + 'the pricing engine and are authoritative; this commentary is a draft for a human to verify and edit.', S.note),
      ...pad(8, S.note)]);
    narrative.split(/\n+/).map(s => s.trim()).filter(Boolean).forEach(p => {
      R.push([txt(p, S.aiBody), ...pad(8, S.aiBody)]);
    });
  }

  const wsR = XLSX.utils.aoa_to_sheet(R);
  wsR['!cols'] = [{ wch: 44 }, { wch: 26 }, { wch: 7 }, { wch: 11 }, { wch: 9 },
                  { wch: 16 }, { wch: 16 }, { wch: 17 }, { wch: 19 }];
  // Catatan: freeze panes TIDAK ditulis. xlsx-js-style (SheetJS community)
  // mengabaikan ws['!freeze'] — diverifikasi dengan membongkar berkas hasil:
  // openpyxl melaporkan freeze_panes=None. Membiarkan barisnya di sini akan
  // tampak seperti fitur yang bekerja padahal tidak.
  const wide = (r) => ({ s: { r, c: 0 }, e: { r, c: 8 } });
  wsR['!merges'] = [];
  R.forEach((row, i) => {
    const c0 = row[0];
    if (!c0 || !c0.s) return;
    if (c0.s === S.title || c0.s === S.sub || c0.s === S.note
        || c0.s === S.aiBody || c0.s === S.aiHead || c0.s === S.section) {
      wsR['!merges'].push(wide(i));
    }
  });
  wsR['!rows'] = R.map((row) => {
    const c0 = row[0];
    if (c0 && c0.s === S.aiBody) return { hpt: 46 };
    if (c0 && c0.s === S.note) return { hpt: 30 };
    if (c0 && c0.s === S.text) return { hpt: 26 };
    return undefined;
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsR, 'Cost Comparison Report');
  XLSX.utils.book_append_sheet(wb, wsA, 'Assumptions');
  XLSX.utils.book_append_sheet(wb, buildReimportSheet(lines), 'Re-import');
  // Disembunyikan supaya pembaca proposal tidak menemukan lembar berisi nama
  // field. Berbeda dengan ws['!freeze'] yang diabaikan tanpa suara oleh
  // xlsx-js-style, Workbook.Sheets[].Hidden BENAR-BENAR tertulis — diverifikasi
  // dengan membongkar berkas hasilnya (openpyxl melaporkan sheet_state='hidden').
  // Urutannya harus sejajar dengan urutan book_append_sheet di atas.
  wb.Workbook = { Sheets: [{ Hidden: 0 }, { Hidden: 0 }, { Hidden: 1 }] };
  XLSX.writeFile(wb, `AWS_Cost_Proposal_${region}_${stamp()}.xlsx`);
}

// ─────────────────────────────────────────────────────────────────────────────
// PDF — jendela cetak browser, tanpa pustaka PDF
// ─────────────────────────────────────────────────────────────────────────────
function exportPdf(ctx) {
  const { lines, computeLine, hoursPerMonth, region, supportPlan, vatCountry,
          fxRate, totals, narrative } = ctx;
  const rows = buildRows(ctx);
  const termYears = Math.max(1, ...rows.map(r => r.leaseYears || 1));

  const body = rows.map(r => {
    const od = tieredOd(r, lines, computeLine, hoursPerMonth);
    const saving = od > 0 ? (1 - r.monthly / od) : 0;
    return `<tr>
      <td>${esc(r.kind)} · ${esc(r.label)}<div class="sm mono">${esc(r.detail)}</div></td>
      <td>${esc(r.commitment)}</td>
      <td class="r">${r.qty}</td>
      <td class="r">${fmt(r.units, 0)} ${esc(r.unit)}</td>
      <td class="r mono">${od === null ? '—' : '$' + fmt(od)}</td>
      <td class="r mono">$${fmt(r.monthly)}</td>
      <td class="r mono">${r.upfront > 0 ? '$' + fmt(r.upfront) : '—'}</td>
      <td class="r">${od > 0 ? (saving * 100).toFixed(1) + '%' : '—'}</td>
      <td class="sm warn">${esc(r.flags)}</td>
    </tr>`;
  }).join('');

  const kv = (l, v, b) => `<tr class="${b ? 'b' : ''}"><td class="r">${esc(l)}</td><td class="r mono">${v}</td></tr>`;
  const overTerm = totals.cash + totals.total * 12 * termYears;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>AWS Cost Proposal — ${esc(region)}</title><style>
@page { size: A4 landscape; margin: 12mm; }
body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #111827; font-size: 11px; margin: 0; }
h1 { font-size: 18px; margin: 0 0 2px; color: #0f766e; }
.meta { font-size: 10px; color: #6b7280; margin-bottom: 12px; }
table { width: 100%; border-collapse: collapse; }
thead { display: table-header-group; } tr { page-break-inside: avoid; }
th { background: #0d9488; color: #fff; font-size: 10px; padding: 5px 6px; text-align: left; border: 1px solid #d1d5db; }
td { padding: 4px 6px; border: 1px solid #e5e7eb; vertical-align: top; }
.r { text-align: right; } .mono { font-family: ui-monospace, Consolas, monospace; }
.sm { font-size: 9px; color: #6b7280; } .warn { color: #b45309; }
.totals { margin-top: 14px; width: 360px; margin-left: auto; }
.totals td { border: none; padding: 3px 6px; }
.totals tr.b td { font-weight: 700; border-top: 1px solid #0d9488; background: #ccfbf1; }
.notes { margin-top: 12px; font-size: 8.5px; color: #9ca3af; line-height: 1.5; }
.ai { margin-top: 14px; border: 1px solid #fb923c; background: #fff7ed; padding: 8px 10px; }
.ai h2 { font-size: 11px; margin: 0 0 4px; color: #7c2d12; }
.ai p { margin: 4px 0; font-size: 10px; color: #431407; }
.ai .lbl { font-size: 8.5px; color: #9a3412; font-style: italic; }
</style></head><body>
<h1>AWS Cost Proposal — ${esc(region)}</h1>
<div class="meta">Generated ${esc(nowText())} · Term ${termYears} year(s) · Support: ${esc(supportPlan)} ·
 VAT: ${esc(vatCountry)} (${totals.vatPct}%) · Hours per month: ${hoursPerMonth}</div>
<table><thead><tr>
<th>Item</th><th>Commitment</th><th class="r">Qty</th><th class="r">Units / mo</th>
<th class="r">On-Demand $/mo</th><th class="r">Selected $/mo</th><th class="r">Upfront</th>
<th class="r">Saving</th><th>Notes</th>
</tr></thead><tbody>${body}</tbody></table>
<table class="totals">
${kv('Monthly recurring', '$' + fmt(totals.recurring), true)}
${kv(`Support — ${supportPlan}`, '$' + fmt(totals.support))}
${kv(`VAT ${totals.vatPct}%`, '$' + fmt(totals.vat))}
${kv('TOTAL PER MONTH', '$' + fmt(totals.total), true)}
${fxRate > 0 ? kv('Equivalent in IDR', 'Rp ' + Math.round(totals.total * fxRate).toLocaleString('id-ID')) : ''}
${totals.cash > 0 ? kv('Cash upfront (month 0)', '$' + fmt(totals.cash)) : ''}
${kv(`Total over ${termYears} year(s)`, '$' + fmt(overTerm), true)}
</table>
<div class="notes">
Monthly totals follow the AWS Pricing Calculator convention: recurring cost only. Upfront is not amortised into the
monthly figure — it is shown separately as a month-0 outflow.<br/>
The Support basis excludes reserved-instance upfront, matching how AWS bills it.
</div>
${narrative && narrative.trim() ? `<div class="ai"><h2>AI-generated commentary — review before use</h2>
<div class="lbl">Written by a language model from the figures above. The numbers are computed by the pricing engine
and are authoritative; this commentary is a draft for a human to verify.</div>
${narrative.split(/\n+/).map(s => s.trim()).filter(Boolean).map(p => `<p>${esc(p)}</p>`).join('')}</div>` : ''}
</body></html>`;

  const w = window.open('', '_blank');
  if (!w) return false;
  w.document.open(); w.document.write(html); w.document.close(); w.focus();
  setTimeout(() => { try { w.print(); } catch (_) {} }, 350);
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Naratif AI
//
// Memakai /api/ai/ask dengan mode:'context' — jalur BEBAS-SQL yang sudah ada di
// AI Service. Sengaja BUKAN endpoint baru: parameter generasi (temperature,
// max_tokens, top_p, seed) dan System Prompt dibaca server dari tabel
// ai_configs lewat aiGenParams()/aiSystemExtra(), sehingga setelan pada halaman
// AI Configuration otomatis berlaku di sini. Endpoint sendiri berarti menyalin
// pengayaan itu dan membiarkannya menyimpang diam-diam.
//
// ANGKA DIPASOK, BUKAN DIMINTA. Model menerima ringkasan yang sudah dihitung dan
// diminta menafsirkannya. Meminta model menghitung berarti mengundang angka
// karangan masuk ke dokumen biaya.
// ─────────────────────────────────────────────────────────────────────────────
function narrativeContext(ctx) {
  const { region, supportPlan, vatCountry, hoursPerMonth, totals, lines, computeLine } = ctx;
  const rows = buildRows(ctx);
  const termYears = Math.max(1, ...rows.map(r => r.leaseYears || 1));
  const items = rows.map(r => {
    const od = tieredOd(r, lines, computeLine, hoursPerMonth);
    return {
      item: `${r.kind} · ${r.label}`,
      commitment: r.commitment,
      qty: r.qty,
      units_per_month: r.units,
      unit: r.unit,
      on_demand_usd_month: od === null ? null : Number(od.toFixed(2)),
      selected_usd_month: Number(r.monthly.toFixed(2)),
      upfront_usd: Number(r.upfront.toFixed(2)),
      saving_pct: od > 0 ? Number(((1 - r.monthly / od) * 100).toFixed(1)) : null,
      flags: r.flags || null,
    };
  });
  const odTotal = items.reduce((s, i) => s + (i.on_demand_usd_month || 0), 0);
  return {
    region, support_plan: supportPlan, vat_country: vatCountry,
    hours_per_month: hoursPerMonth, term_years: termYears, items,
    totals_usd_month: {
      on_demand_equivalent: Number(odTotal.toFixed(2)),
      recurring: Number(totals.recurring.toFixed(2)),
      support: Number(totals.support.toFixed(2)),
      vat: Number(totals.vat.toFixed(2)),
      total: Number(totals.total.toFixed(2)),
    },
    cash_upfront_month0_usd: Number(totals.cash.toFixed(2)),
    total_over_term_usd: Number((totals.cash + totals.total * 12 * termYears).toFixed(2)),
  };
}

async function fetchNarrative(ctx) {
  const question = [
    'Write a short commentary for an AWS cost proposal, in English, for IT management.',
    'Use ONLY the figures in the supplied context. Do not compute new numbers and do not invent line items.',
    'Cover in 3 to 5 short paragraphs: what this estimate covers and the headline monthly and over-term cost;',
    'where the savings come from and what commitment is being made;',
    'the cash-flow implication of any upfront payment;',
    'and risks or caveats a reviewer should check before approving, including any flags present in the items.',
    'Be factual and measured. Plain paragraphs only — no headings, no bullet points, no markdown.',
    'If a figure is absent from the context, say so rather than estimating it.',
  ].join(' ');

  const { data } = await api.post('/api/ai/ask', {
    source: 'aws_pricing',
    question,
    mode: 'context',
    context: JSON.stringify(narrativeContext(ctx)),
    context_label: 'AWS Pricing Calculator — active estimate',
    lang: 'en',
  });
  if (data && data.error) throw new Error(data.error);
  const text = (data && (data.answer || data.text)) || '';
  if (!text.trim()) throw new Error('AI Service returned an empty answer.');
  return text;
}

// ─────────────────────────────────────────────────────────────────────────────

export default function AwsPricingExport(props) {
  const { t, lines } = props;
  const [warn, setWarn] = useState(null);
  const [narrative, setNarrative] = useState('');
  const [busy, setBusy] = useState(false);
  const empty = !lines || !lines.length;

  const run = (fn) => {
    setWarn(null);
    try {
      const ok = fn({ ...props, narrative });
      if (ok === false) {
        setWarn(tf(t, 'awsp.exp.popupBlocked',
          'The print window was blocked — allow popups for this site.'));
      }
    } catch (e) { setWarn(e?.message || String(e)); }
  };

  const genNarrative = async () => {
    setWarn(null); setBusy(true);
    try { setNarrative(await fetchNarrative(props)); }
    catch (e) {
      // Kegagalan AI TIDAK membatalkan ekspor: pengguna tetap bisa menekan Excel
      // atau PDF dan mendapat angka yang benar, hanya tanpa blok naratif.
      setWarn(tf(t, 'awsp.exp.narrativeFailed', 'Narrative unavailable')
        + ': ' + (e?.message || String(e)));
    } finally { setBusy(false); }
  };

  const btn = 'px-2.5 py-1.5 rounded border border-gray-300 bg-white text-xs font-medium text-gray-700 '
    + 'hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5';

  return (
    <div className="flex items-center gap-2">
      {warn && <span className="text-xs text-amber-700 max-w-xs truncate" title={warn}>{warn}</span>}
      {narrative && !busy && !warn && (
        <span className="text-xs text-emerald-700" title={narrative}>
          {tf(t, 'awsp.exp.narrativeReady', 'Narrative ready')}
        </span>
      )}
      <button className={btn} onClick={genNarrative} disabled={empty || busy}
              title={tf(t, 'awsp.exp.narrative', 'AI narrative')}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-500" />
              : <Sparkles className="w-3.5 h-3.5 text-orange-500" />}
        {tf(t, 'awsp.exp.narrative', 'AI narrative')}
      </button>
      <button className={btn} onClick={() => run(exportExcel)} disabled={empty}
              title={tf(t, 'awsp.exp.excel', 'Excel')}>
        <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" /> {tf(t, 'awsp.exp.excel', 'Excel')}
      </button>
      <button className={btn} onClick={() => run(exportPdf)} disabled={empty}
              title={tf(t, 'awsp.exp.pdf', 'PDF')}>
        <FileText className="w-3.5 h-3.5 text-red-500" /> {tf(t, 'awsp.exp.pdf', 'PDF')}
      </button>
    </div>
  );
}
